import { Card, Divider, Spin, Typography } from "antd";
import { ReactNode } from "react";
import { MetricChartEmpty } from "./MetricChartRender";

// Shared building blocks for the dashboard's full-width insight sections: a compact headline
// stat strip plus the uniform chart-card shell every focused chart renders in. One section =
// one server aggregate feeding an InsightStrip and a row of InsightChartCards.

/** A labelled run of strip items — a strip that mixes domains can group them under muted headings. */
export interface InsightStripGroup {
  readonly label: string;
  readonly items: ReactNode[];
}

// One " · "-separated headline line. Items render as-is, so a caller can warning-colour a stat
// (e.g. a scoring backlog) or attach a tooltip. When `groups` is given, the same items render
// under muted small-caps group labels with a light divider between groups; flat `items` callers
// are untouched.
export function InsightStrip(props: {
  readonly items?: ReactNode[];
  readonly groups?: InsightStripGroup[];
}) {
  if (props.groups) {
    return (
      <Typography.Text type="secondary">
        {props.groups.map((group, g) => (
          <span key={group.label}>
            {g > 0 && <Divider type="vertical" />}
            <span className="text-[10px] uppercase tracking-wider">{group.label}</span>{" "}
            {group.items.map((item, i) => (
              <span key={i}>
                {i > 0 && " · "}
                {item}
              </span>
            ))}
          </span>
        ))}
      </Typography.Text>
    );
  }
  return (
    <Typography.Text type="secondary">
      {(props.items ?? []).map((item, i) => (
        <span key={i}>
          {i > 0 && " · "}
          {item}
        </span>
      ))}
    </Typography.Text>
  );
}

export interface InsightChartCardProps {
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly loading: boolean;
  readonly error: boolean;
  readonly children: ReactNode;
}

// Uniform chart-card shell: shares the section row (min-width + flex, matching the descriptor
// cards) and carries the loading/error states so each chart only renders its happy path.
export function InsightChartCard(props: InsightChartCardProps) {
  return (
    <div className="min-w-[340px] flex-1">
      <Card title={props.title} className="flex-auto">
        <Spin spinning={props.loading}>
          {props.error ? (
            <MetricChartEmpty label="Something went wrong" />
          ) : (
            <div className="flex flex-col gap-2">
              {props.subtitle && (
                <Typography.Text type="secondary" className="text-xs">
                  {props.subtitle}
                </Typography.Text>
              )}
              {props.children}
            </div>
          )}
        </Spin>
      </Card>
    </div>
  );
}
